import { PlanogramPegboardPlacement } from "@CommonTypes/merchflow/pog/pegboard";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Color, Flex } from "src/elements";
import {
  getPegboardFacingChangeColor,
  getPegboardFacingChangeLabelKey,
} from "./pegboardFacingChange";
import { ProductImage } from "../Item/Image";
import styled from "styled-components";
import { TextElement } from "src/elements/Text/Text";
import { useTranslation } from "src/i18n";
import {
  labelOptionAtom,
  planogramImagesModeAtom,
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
  planogramSelectedItemsAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { isPegboardPlacementSelection } from "src/components/Planogram/common/planogramSelection";
import { resolvePlanogramProductLabel } from "src/components/Planogram/common/planogramItemLabel";
import { SelectionOutliner } from "../Item/SelectionOutliner";
import { useFocusableElement } from "src/hooks";
import { Transition } from "src/utils";
import { useEffect, useMemo, useRef } from "react";
import { baseHighlightsAtom, highlightsModeAtom } from "src/components/Planogram/store/atoms";
import { getHighlightColor } from "../Item/store/highlight";
import { planogramLegendAtom } from "src/components/PlanogramExtensions/PlanogramLegend";
import { PogAtom } from "../../store/types";
const PegboardPlacementElement = styled(Flex)<{
  anchorNoLeft: number;
  anchorNoTop: number;
  holesGapHorizontal: number;
  holesGapVertical: number;
  pegboardPlacementWidth: number;
  pegboardPlacementHeight: number;
  scaleX: number;
  scaleY: number;
  isSelected: boolean;
  isNotHighlighted: boolean;
  tabIndex?: number;
}>`
  position: absolute;
  left: ${({ anchorNoLeft, holesGapHorizontal, scaleX }) =>
    `calc((${anchorNoLeft} - 1) * ${holesGapHorizontal}px * ${scaleX})`};
  top: ${({ anchorNoTop, holesGapVertical, scaleY }) =>
    `calc((${anchorNoTop} - 1) * ${holesGapVertical}px * ${scaleY})`};
  width: ${({ pegboardPlacementWidth, scaleX }) => `calc(${pegboardPlacementWidth}px * ${scaleX})`};
  height: ${({ pegboardPlacementHeight, scaleY }) =>
    `calc(${pegboardPlacementHeight}px * ${scaleY})`};
  overflow: visible;
  user-select: none;
  cursor: pointer;
  pointer-events: all;
  opacity: ${({ isNotHighlighted }) => isNotHighlighted && "0.5"};
  /* Placements must stay above the pegboard hole grid and background fill. */
  z-index: ${({ isSelected }) => (isSelected ? 2 : 1)};

  > * {
    pointer-events: none;
  }
`;

// One facing per pegboard hole, absolutely positioned within the placement's
// bounding box at its own hole grid coordinate.
const PegboardFacingCell = styled(Flex)<{
  offsetLeft: number;
  offsetTop: number;
  facingWidth: number;
  facingHeight: number;
  scaleX: number;
  scaleY: number;
}>`
  position: absolute;
  left: ${({ offsetLeft, scaleX }) => `calc(${offsetLeft}px * ${scaleX})`};
  top: ${({ offsetTop, scaleY }) => `calc(${offsetTop}px * ${scaleY})`};
  width: ${({ facingWidth, scaleX }) => `calc(${facingWidth}px * ${scaleX})`};
  height: ${({ facingHeight, scaleY }) => `calc(${facingHeight}px * ${scaleY})`};
  overflow: visible;
  background-color: rgba(255, 255, 255, 0.12);
  border-radius: 2px;
  align-items: center;
  justify-content: center;
`;

const PegboardFacingContent = styled(Flex)`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 2px;
  align-items: center;
  justify-content: center;
`;

const PegboardPlacementLabel = styled(TextElement)`
  padding: 2px;
  text-align: center;
  word-break: break-all;
`;

const Highlighter = styled(Flex)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
  border: 1px solid rgba(0, 0, 0, 0.9);
  background: yellow;
  border-radius: 3px;
  animation: pulsate infinite ${Transition.turtle};

  @keyframes pulsate {
    0% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.4;
    }
  }
`;

const Highlight = styled(Flex)<{ highlightColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ highlightColor }) => highlightColor};
  opacity: 0.5;
`;

// Corner badge flagging how a facing changed in the update API response (color per changeType).
const ChangeBadge = styled.div<{ $badgeColor: string }>`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid ${Color.white};
  background: ${({ $badgeColor }) => $badgeColor};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  z-index: 11;
`;

interface Props {
  pegboardPlacement: PlanogramPegboardPlacement;
  holesGapHorizontal: number;
  holesGapVertical: number;
  scaleX: number;
  scaleY: number;
  isHighlighted?: boolean;
  isScrolledToView?: boolean;
  pogAtom: PogAtom;
}

export const PegboardPlacement = ({
  pegboardPlacement,
  holesGapHorizontal,
  holesGapVertical,
  scaleX,
  scaleY,
  isHighlighted = false,
  isScrolledToView = false,
  pogAtom,
}: Props) => {
  const { setTab } = usePlanogramPanel();
  const { t } = useTranslation("common");
  const pog = useAtomValue(pogAtom);
  const [highlightsMode] = useAtom(highlightsModeAtom);
  const [baseHighlights] = useAtom(baseHighlightsAtom);
  const labelOption = useAtomValue(labelOptionAtom);
  const imagesMode = useAtomValue(planogramImagesModeAtom);
  const planogramLegend = useAtomValue(planogramLegendAtom);
  const selectedItems = useAtomValue(planogramSelectedItemsAtom);
  const setSelectedItems = useSetAtom(planogramSelectedItemsAtom);
  const setHoveredPegboardPlacement = useSetAtom(planogramHoveredPegboardPlacementAtom);
  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const setHoveredContainer = useSetAtom(planogramHoveredContainerAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const refPlacement = useRef<HTMLDivElement>(null);
  const { focus: focusPlacement } = useFocusableElement<HTMLDivElement>(refPlacement);
  // Pegboard placement selection is currently single-select. Clicking a placement replaces
  // selected items with just that placement (see onClick below).
  const selectedPegboardPlacement = selectedItems.find(isPegboardPlacementSelection);

  // A pegboard placement renders one product facing per hole. Empty placements are skipped so
  // the DOM renderer matches the canvas renderer and never invents a facing coordinate.
  const holes = pegboardPlacement.holes;
  const leftAnchorHoleNo = holes.length > 0 ? Math.min(...holes.map((hole) => hole.holeNoLeft)) : 1;
  const topAnchorHoleNo = holes.length > 0 ? Math.min(...holes.map((hole) => hole.holeNoTop)) : 1;
  const rightAnchorHoleNo =
    holes.length > 0 ? Math.max(...holes.map((hole) => hole.holeNoLeft)) : 1;
  const bottomAnchorHoleNo =
    holes.length > 0 ? Math.max(...holes.map((hole) => hole.holeNoTop)) : 1;

  const unitWidth = pegboardPlacement.pegProduct.unitWidth;
  const unitHeight = pegboardPlacement.pegProduct.unitHeight;
  // Bounding box spanning every hole so the wrapper stays a single interactive target.
  const placementWidth = (rightAnchorHoleNo - leftAnchorHoleNo) * holesGapHorizontal + unitWidth;
  const placementHeight = (bottomAnchorHoleNo - topAnchorHoleNo) * holesGapVertical + unitHeight;

  const labelText = resolvePlanogramProductLabel(
    pegboardPlacement.pegProduct,
    labelOption,
    t("na"),
  );

  // All facings of a placement share the product's changeType, so every hole gets the badge.
  const changeColor = getPegboardFacingChangeColor(pegboardPlacement.pegProduct);
  const changeType = pegboardPlacement.pegProduct.updateStatus?.changeType;
  const changeTypeLabelKey = getPegboardFacingChangeLabelKey(changeType);

  const highlight = useMemo(() => {
    if (!highlightsMode || !pog) return false;

    if (baseHighlights) {
      for (const highlight of baseHighlights.filter((highlight) => highlight.enabled)) {
        const highlightColor = getHighlightColor(
          highlight,
          pegboardPlacement.pegProduct,
          pog,
          planogramLegend,
        );
        if (highlightColor) {
          return highlightColor;
        }
      }
    }

    return false;
  }, [baseHighlights, highlightsMode, pegboardPlacement.pegProduct, planogramLegend, pog]);

  const isSelected = selectedPegboardPlacement?.uniqueId === pegboardPlacement.uniqueId;

  useEffect(() => {
    if (isHighlighted && isScrolledToView) {
      refPlacement.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  }, [isHighlighted, isScrolledToView]);

  if (holes.length === 0) {
    return null;
  }

  const onPointerEnter = () => {
    setHoveredItem(null);
    setHoveredContainer(null);
    setHoveredShelf(null);
    setHoveredPegboardPlacement(pegboardPlacement);
  };

  const onPointerLeave = () => {
    setHoveredPegboardPlacement(null);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();

    focusPlacement();
    setSelectedItems([pegboardPlacement]);
    setTab("PRODUCT_DETAILS");
  };

  return (
    <PegboardPlacementElement
      ref={refPlacement}
      tabIndex={-1}
      anchorNoLeft={leftAnchorHoleNo}
      anchorNoTop={topAnchorHoleNo}
      holesGapHorizontal={holesGapHorizontal}
      holesGapVertical={holesGapVertical}
      pegboardPlacementWidth={placementWidth}
      pegboardPlacementHeight={placementHeight}
      scaleX={scaleX}
      scaleY={scaleY}
      isSelected={isSelected}
      isNotHighlighted={highlightsMode && !highlight}
      onPointerDown={onPointerDown}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
    >
      {holes.map((hole) => (
        <PegboardFacingCell
          key={`${hole.holeNoLeft}-${hole.holeNoTop}-${hole.depth}`}
          offsetLeft={(hole.holeNoLeft - leftAnchorHoleNo) * holesGapHorizontal}
          offsetTop={(hole.holeNoTop - topAnchorHoleNo) * holesGapVertical}
          facingWidth={unitWidth}
          facingHeight={unitHeight}
          scaleX={scaleX}
          scaleY={scaleY}
        >
          {isSelected && <SelectionOutliner />}

          {isHighlighted && <Highlighter />}

          <PegboardFacingContent>
            {imagesMode ? (
              <ProductImage
                productCode={pegboardPlacement.pegProduct.productCode}
                size="small"
                fixedSize="100%"
              />
            ) : (
              <PegboardPlacementLabel
                variant="caption3"
                textShadow="none"
                textAlign="center"
                alignContent="center"
                wordBreak="break-all"
              >
                {labelText}
              </PegboardPlacementLabel>
            )}
          </PegboardFacingContent>

          {highlight && <Highlight highlightColor={highlight} />}

          {changeColor && (
            <ChangeBadge
              $badgeColor={changeColor}
              role="img"
              aria-label={
                changeTypeLabelKey
                  ? t("pegboardFacingChange.indicator", {
                      changeType: t(changeTypeLabelKey),
                    })
                  : undefined
              }
            />
          )}
        </PegboardFacingCell>
      ))}
    </PegboardPlacementElement>
  );
};
